// src/components/AddMaintenanceModal.jsx
import { useEffect, useState } from 'react';
import { Modal, Button, NumberInput, Select, Stack, Textarea, TextInput, Group, FileInput, Tooltip } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconCalendar, IconCurrencyLira, IconBuildingStore, IconUpload, IconDownload } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import api from '../../api/axiosConfig';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

export default function AddMaintenanceModal({ opened, close, onSuccess, maintenanceToEdit }) {
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  
  // Araçları çek
  useEffect(() => {
    const fetchVehicles = async () => {
        try {
            const res = await api.get('/vehicles/');
            const data = res.data.map(v => ({
                value: String(v.id),
                label: `${v.plate} - ${v.brand}`
            }));
            setVehicles(data);
        } catch (err) {
            console.error("Araçlar çekilemedi", err);
        }
    };
    fetchVehicles();
  }, []);

  const form = useForm({
    initialValues: {
      vehicle_id: '',
      vehicle_plate: '',
      maintenance_type: 'Periyodik Bakım',
      date: new Date(),
      km_at_maintenance: '',
      next_maintenance_km: '',
      cost: '',
      provider: '',
      description: '',
      invoice_file: null // Fatura dosyası
    },
    validate: {
      vehicle_id: (value) => (!value ? 'Lütfen bir araç seçin' : null),
      cost: (value) => (value < 0 ? 'Tutar negatif olamaz' : null),
      provider: (value) => (value && value.length < 2 ? 'Firma ismi giriniz' : null),
    },
  });

  useEffect(() => {
    if (maintenanceToEdit) {
      form.setValues({
        vehicle_id: String(maintenanceToEdit.vehicle_id || ''), 
        vehicle_plate: maintenanceToEdit.vehicle_plate || '',
        maintenance_type: maintenanceToEdit.maintenance_type,
        date: new Date(maintenanceToEdit.date), 
        km_at_maintenance: maintenanceToEdit.km_at_maintenance,
        next_maintenance_km: maintenanceToEdit.next_maintenance_km || '',
        cost: maintenanceToEdit.cost,
        provider: maintenanceToEdit.provider || '',
        description: maintenanceToEdit.description || '',
        invoice_file: null // Düzenlemede her zaman boş başlar, yeni dosya seçilirse ezilir
      });
    } else {
      form.reset();
    }
  }, [maintenanceToEdit, opened]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('vehicle_id', values.vehicle_id); 
      formData.append('maintenance_type', values.maintenance_type);
      formData.append('date', dayjs(values.date).format('YYYY-MM-DD'));
      formData.append('km_at_maintenance', values.km_at_maintenance);
      formData.append('cost', values.cost);
      
      if (values.next_maintenance_km) formData.append('next_maintenance_km', values.next_maintenance_km);
      if (values.provider) formData.append('provider', values.provider);
      if (values.description) formData.append('description', values.description);
      
      // Dosya seçilmişse ekle
      if (values.invoice_file) {
        formData.append('invoice_file', values.invoice_file);
      }

      if (maintenanceToEdit) {
        await api.put(`/vehicles/maintenance/${maintenanceToEdit.id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await api.post('/vehicles/maintenance/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      onSuccess(); 
      close();    
    } catch (error) {
      console.error("İşlem hatası:", error);
      alert("Kayıt işlemi sırasında bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
      try {
          const response = await api.get(`/vehicles/maintenance/${maintenanceToEdit.id}/invoice`, {
              responseType: 'blob'
          });
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Fatura_${maintenanceToEdit.maintenance_type}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
      } catch (error) {
          console.error("İndirme hatası", error);
          alert("Fatura bulunamadı veya indirilemedi.");
      }
  };

  return (
    <Modal 
        opened={opened} 
        onClose={close} 
        title={maintenanceToEdit ? "Bakım Kaydını Düzenle" : "Yeni Bakım Kaydı Gir"} 
        centered 
        size="lg"
        overlayProps={{ blur: 3 }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Select
            label="Araç Seçimi"
            placeholder="Plaka seçiniz"
            data={vehicles}
            searchable
            required
            {...form.getInputProps('vehicle_id')} 
          />

          <Group grow>
            <Select
                label="İşlem Türü"
                data={['Periyodik Bakım', 'Ağır Bakım', 'Lastik Değişimi', 'Muayene', 'Arıza/Tamir', 'Yağ Değişimi', 'Kaporta/Boya', 'Diğer']}
                {...form.getInputProps('maintenance_type')}
            />
            <DatePickerInput
                label="İşlem Tarihi"
                locale="tr"
                valueFormat="DD.MM.YYYY"
                leftSection={<IconCalendar size={16} />}
                {...form.getInputProps('date')}
            />
          </Group>

          <Group grow>
             <TextInput
                label="Bakımı Yapan Firma / Servis"
                placeholder="Örn: Bosch Car Service"
                leftSection={<IconBuildingStore size={16} />}
                required
                {...form.getInputProps('provider')}
             />
             <NumberInput
                label="Toplam Maliyet"
                leftSection={<IconCurrencyLira size={16} />}
                thousandSeparator=","
                min={0}
                required
                {...form.getInputProps('cost')}
            />
          </Group>

          <Group grow>
            <NumberInput
                label="İşlem Yapılan KM"
                thousandSeparator=","
                min={0}
                required
                {...form.getInputProps('km_at_maintenance')}
            />
            <NumberInput
                label="Bir Sonraki Bakım KM (Tahmini)"
                thousandSeparator=","
                min={0}
                description="Uyarı sistemi için gereklidir"
                {...form.getInputProps('next_maintenance_km')}
            />
          </Group>

          <Textarea
            label="Açıklama / Yapılan İşlemler"
            placeholder="Örn: Yağ filtresi, hava filtresi değişti. Balatalar kontrol edildi."
            autosize
            minRows={2}
            {...form.getInputProps('description')}
          />

          {/* FATURA YÜKLEME VE İNDİRME ALANI */}
          <Group align="flex-end" grow>
             <FileInput
                label="Fatura / Servis Belgesi Ekle"
                placeholder="PDF, JPG veya PNG seçin"
                accept="application/pdf,image/png,image/jpeg"
                leftSection={<IconUpload size={16} />}
                clearable
                {...form.getInputProps('invoice_file')}
             />
             
             {maintenanceToEdit?.invoice_path && (
                <Tooltip label="Sistemdeki Mevcut Faturayı İndir">
                    <Button variant="light" color="violet" leftSection={<IconDownload size={16}/>} onClick={handleDownloadInvoice}>
                        Mevcut Faturayı İndir
                    </Button>
                </Tooltip>
             )}
          </Group>

          <Button type="submit" fullWidth mt="md" loading={loading} color="blue">
            {maintenanceToEdit ? "Güncelle" : "Kaydet"}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}